// Leaderboard rebuilt with extension-synced platform data
// All platforms: OnlyFans (VIP + Free), JustForFans, X/Twitter, Studios, FapHouse, RFC
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import AppLayout from "../components/AppLayout";
import OperationsPulse from "../components/OperationsPulse";
import {
  Trophy, DollarSign, RefreshCw, Crown, Medal, Award,
  ExternalLink, ShoppingCart, Building2, TrendingUp,
  Users, MessageSquare, BarChart3, Twitter,
} from "lucide-react";

const SORT_OPTIONS = [
  { key: "total", label: "Total Revenue", field: "total_revenue" },
  { key: "subscribers", label: "Subscribers", field: "subscribers" },
  { key: "followers", label: "Followers", field: "followers" },
  { key: "engagement", label: "Engagement", field: "engagement_score" },
];

const PLATFORM_COLORS = {
  onlyfans_vip: "#A855F7",
  onlyfans_free: "#22C55E",
  justforfans: "#F59E0B",
  faphouse: "#E11D48",
  rawfuckclub: "#FBBF24",
};

const PLATFORM_LABELS = {
  onlyfans_vip: "OnlyFans VIP",
  onlyfans_free: "OnlyFans Free",
  justforfans: "JustForFans",
  faphouse: "Faphouse",
  rawfuckclub: "RawFuckClub",
};

const PLATFORMS = Object.keys(PLATFORM_LABELS);

export default function CRMLeaderboard() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("total");
  const [search, setSearch] = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [jffTopSellers, setJffTopSellers] = useState([]);
  const [jffStoreSales, setJffStoreSales] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      // Load extension stats for all platforms
      const results = await Promise.allSettled(
        PLATFORMS.map((p) =>
          api.get(`/api/platform-data/stats/${p}`).catch(() => ({ data: null }))
        )
      );

      const platformData = {};
      PLATFORMS.forEach((platform, i) => {
        if (results[i].status === "fulfilled" && results[i].value?.data) {
          platformData[platform] = results[i].value.data;
        }
      });

      setData(platformData);
      const [topSellersResult, storeSalesResult] = await Promise.all([
        api.get("/api/platform-data/entities?platform=justforfans&entity_type=jff_top_seller&limit=200")
          .catch(() => ({ data: { entities: [] } })),
        api.get("/api/platform-data/entities?platform=justforfans&entity_type=jff_store_sale_item&limit=200")
          .catch(() => ({ data: { entities: [] } })),
      ]);
      const readItem = (entity) => {
        const values = entity.normalized_payload || entity.normalized || {};
        return { ...values, account_id: entity.account_id, observed_at: entity.captured_at || entity.last_seen };
      };
      setJffTopSellers((topSellersResult.data?.entities || []).map(readItem)
        .filter((item) => item.item_title).sort((a, b) => (Number(b.reported_revenue) || 0) - (Number(a.reported_revenue) || 0)));
      setJffStoreSales((storeSalesResult.data?.entities || []).map(readItem)
        .filter((item) => item.item_title).sort((a, b) => (Number(b.reported_revenue) || 0) - (Number(a.reported_revenue) || 0)));
    } catch (e) {
      toast.error("Failed to load leaderboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Create leaderboard entries from platform data
  const entries = useMemo(() => {
    const items = [];

    Object.entries(data).forEach(([platform, stats]) => {
      if (!stats) return;

      // Filter by platform if selected
      if (platformFilter !== "all" && platform !== platformFilter) return;

      items.push({
        platform,
        name: PLATFORM_LABELS[platform],
        total_revenue: platform === "justforfans"
          ? Number(stats.ledger_total_revenue) || 0
          : stats.total_revenue || 0,
        reported_total_revenue: stats.reported_total_revenue ?? null,
        subscribers: stats.subscribers || 0,
        followers: stats.followers || 0,
        posts_count: stats.posts_count || 0,
        engagement_score:
          (stats.subscribers || 0) + (stats.followers || 0) * 0.5,
        last_synced: stats.last_synced_at,
        earnings: stats.earnings || {},
        transactions: stats.recent_transactions || [],
      });
    });

    const meaningful = items.filter((item) =>
      item.total_revenue > 0 || item.subscribers > 0 || item.followers > 0
      || item.posts_count > 0 || item.transactions.length > 0
    );

    // Apply search
    const term = search.trim().toLowerCase();
    const filtered = meaningful.filter(
      (item) => !term || item.name.toLowerCase().includes(term)
    );

    // Sort
    const sortField =
      SORT_OPTIONS.find((s) => s.key === sort)?.field || "total_revenue";
    return filtered.sort((a, b) => (b[sortField] || 0) - (a[sortField] || 0));
  }, [data, sort, search, platformFilter]);

  const aggregated = useMemo(() => entries.reduce((totals, platform) => ({
    revenue: totals.revenue + platform.total_revenue,
    subscribers: totals.subscribers + platform.subscribers,
    followers: totals.followers + platform.followers,
    posts: totals.posts + platform.posts_count,
    transactions: totals.transactions + platform.transactions.length,
  }), { revenue: 0, subscribers: 0, followers: 0, posts: 0, transactions: 0 }), [entries]);
  const jffMassMessageMetrics = Object.entries(data.justforfans?.reported_metrics || {})
    .flatMap(([key, metric]) => {
      const match = key.match(/^jff_mass_message_(3m|6m|12m|all)_(total_mass_messages|total_recipients|total_purchases|total_earned)$/);
      return match ? [{ key, range: match[1], kind: match[2], metric }] : [];
    });
  const latestMassMessageCapture = jffMassMessageMetrics.map(({ metric }) => metric.captured_at).filter(Boolean).sort().at(-1);
  const latestMassMessageMetrics = jffMassMessageMetrics.filter(({ metric }) => metric.captured_at === latestMassMessageCapture);
  const massMessageRange = latestMassMessageMetrics[0]?.range;
  const massMessageRangeLabel = ({ all: "All Time", "3m": "Last 3 Months", "6m": "Last 6 Months", "12m": "Last 12 Months" })[massMessageRange] || "";
  const massMessageMetricLabels = {
    total_mass_messages: "Mass messages",
    total_recipients: "Recipients",
    total_purchases: "Purchases",
    total_earned: "Reported earned",
  };

  return (
    <AppLayout
      title="Platform Revenue Leaderboard"
      overline="EXTENSION DATA · CROSS-PLATFORM · LIVE"
      subtitle={`${entries.length} platforms connected · $${aggregated.revenue.toLocaleString()} total revenue`}
      actions={
        <button
          onClick={load}
          data-testid="leaderboard-refresh"
          className="btn-ghost inline-flex items-center gap-2 text-[12px]">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />{" "}
          Refresh
        </button>
      }>
      {/* Data Source Banner */}
      <div
        className="mb-4 rounded-lg border p-3 flex items-start gap-3 text-[12px]"
        style={{
          borderColor: "var(--gio-border)",
          background: "var(--gio-surface)",
          color: "var(--gio-muted-2)",
        }}>
        <TrendingUp
          size={14}
          className="mt-0.5 shrink-0"
          style={{ color: "var(--gio-primary)" }}
        />
        <div>
          <span className="font-bold" style={{ color: "var(--gio-text)" }}>
            Extension-synced data:
          </span>{" "}
          Real-time metrics from OnlyFans VIP, OnlyFans Free, JustForFans, Faphouse,
          and RawFuckClub. All revenue, subscriber, and engagement
          data is pulled directly from platform extension sync.
        </div>
      </div>

      {/* Overview */}
      <OperationsPulse
        title="Cross-Platform Operations Pulse"
        subtitle="Live extension-synced metrics across all connected platforms."
        accent="#f59e0b"
        metrics={[
          {
            label: "Platforms",
            value: entries.length,
            detail: "connected",
          },
          {
            label: "Total Revenue",
            value: `$${aggregated.revenue.toLocaleString()}`,
            color: "#22C55E",
            detail: "all platforms",
          },
          {
            label: "Subscribers",
            value: aggregated.subscribers.toLocaleString(),
            color: "#00AFF0",
            detail: "across platforms",
          },
          {
            label: "Followers",
            value: aggregated.followers.toLocaleString(),
            color: "#A855F7",
            detail: "total audience",
          },
        ]}
        gap="⚠️ Data refreshes when Chrome extension syncs. Manual refresh available above."
      />

      {/* Grand Totals */}
      <div
        className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6"
        data-testid="lb-totals">
        <GrandStat
          testid="lb-grand-revenue"
          accent
          label="Total Revenue"
          icon={<DollarSign size={14} />}
          value={`$${aggregated.revenue.toLocaleString()}`}
          sub={`${entries.length} platforms`}
        />
        <GrandStat
          testid="lb-grand-subscribers"
          tint="#00AFF0"
          label="Subscribers"
          icon={<Users size={14} />}
          value={aggregated.subscribers.toLocaleString()}
          sub="premium audience"
        />
        <GrandStat
          testid="lb-grand-followers"
          tint="#A855F7"
          label="Followers"
          icon={<Users size={14} />}
          value={aggregated.followers.toLocaleString()}
          sub="total reach"
        />
        <GrandStat
          testid="lb-grand-posts"
          tint="#22C55E"
          label="Posts"
          icon={<MessageSquare size={14} />}
          value={aggregated.posts.toLocaleString()}
          sub="content volume"
        />
        <GrandStat
          testid="lb-grand-transactions"
          label="Transactions"
          icon={<ShoppingCart size={14} />}
          value={aggregated.transactions.toLocaleString()}
          sub="tracked sales"
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="leaderboard-search"
          placeholder="Search platforms…"
          className="gio-input flex-1 min-w-[200px] max-w-md"
        />

        {/* Platform filter */}
        <div
          className="flex items-center gap-1 border rounded-lg p-1"
          style={{ borderColor: "var(--gio-border)" }}>
          <span
            className="text-[10px] font-mono-ui uppercase tracking-wider px-2"
            style={{ color: "var(--gio-muted-2)" }}>
            Platform:
          </span>
          {[
            { k: "all", l: "All" },
            { k: "onlyfans_vip", l: "OF VIP" },
            { k: "onlyfans_free", l: "OF Free" },
            { k: "justforfans", l: "JFF" },
            { k: "faphouse", l: "FH" },
            { k: "rawfuckclub", l: "RFC" },
          ].map((o) => (
            <button
              key={o.k}
              onClick={() => setPlatformFilter(o.k)}
              className="px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider rounded transition"
              style={{
                background:
                  platformFilter === o.k
                    ? "var(--gio-primary)"
                    : "transparent",
                color:
                  platformFilter === o.k ? "#000" : "var(--gio-muted-2)",
              }}>
              {o.l}
            </button>
          ))}
        </div>

        {/* Sort */}
        <div
          className="flex items-center gap-1 border rounded-lg p-1"
          style={{ borderColor: "var(--gio-border)" }}
          data-testid="leaderboard-sort">
          <span
            className="text-[10px] font-mono-ui uppercase tracking-wider px-2"
            style={{ color: "var(--gio-muted-2)" }}>
            Sort:
          </span>
          {SORT_OPTIONS.map((o) => (
            <button
              key={o.key}
              onClick={() => setSort(o.key)}
              data-testid={`leaderboard-sort-${o.key}`}
              className="px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider rounded transition"
              style={{
                background: sort === o.key ? "var(--gio-primary)" : "transparent",
                color: sort === o.key ? "#000" : "var(--gio-muted-2)",
              }}>
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* Top 3 Podium */}
      {entries.length >= 3 && (
        <div className="grid grid-cols-3 gap-3 mb-6" data-testid="lb-podium">
          <PodiumCard rank={2} entry={entries[1]} />
          <PodiumCard rank={1} entry={entries[0]} />
          <PodiumCard rank={3} entry={entries[2]} />
        </div>
      )}

      {/* Platform Table */}
      <div
        className="rounded-lg border overflow-hidden"
        style={{ borderColor: "var(--gio-border)" }}
        data-testid="lb-table">
        {!loading && entries.length > 0 && (
          <div
            className="hidden md:grid grid-cols-[48px_1fr_120px_120px_140px_36px] gap-3 px-4 py-2 border-b text-[10px] uppercase tracking-wider font-mono-ui"
            style={{
              borderColor: "var(--gio-border)",
              color: "var(--gio-muted-3)",
              background: "var(--gio-surface)",
            }}>
            <div>Rank</div>
            <div>Platform</div>
            <div className="text-right">Subscribers</div>
            <div className="text-right">Followers</div>
            <div className="text-right">Revenue</div>
            <div />
          </div>
        )}
        {loading && (
          <div
            className="p-8 text-center text-[12px]"
            style={{ color: "var(--gio-muted-3)" }}>
            Loading extension data…
          </div>
        )}
        {!loading && entries.length === 0 && (
          <div
            className="p-12 text-center text-[12px]"
            style={{ color: "var(--gio-muted-3)" }}
            data-testid="lb-empty">
            No platform data synced yet. Connect platforms via Chrome extension.
          </div>
        )}
        {entries.map((entry, i) => (
          <Row key={entry.platform} rank={i + 1} entry={entry} />
        ))}
      </div>

      {(platformFilter === "all" || platformFilter === "justforfans") && <section className="mt-8 border-t pt-4" style={{ borderColor: "var(--gio-border)" }} data-testid="lb-jff-item-performance">
        <div className="mb-1 flex items-center gap-2">
          <BarChart3 size={15} style={{ color: PLATFORM_COLORS.justforfans }} />
          <h2 className="text-sm font-bold" style={{ color: "var(--gio-text)" }}>JFF content sales</h2>
        </div>
        <p className="mb-4 text-[11px]" style={{ color: "var(--gio-muted-3)" }}>Platform-reported item data · separate from platform ranking and Finance ledger totals · captured visible rows only</p>
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="border-t pt-3" style={{ borderColor: "var(--gio-border)" }}>
            <h3 className="mb-2 text-xs font-semibold" style={{ color: "var(--gio-text)" }}>Top Sellers · all time</h3>
            {jffTopSellers.length === 0 ? <p className="py-4 text-xs" style={{ color: "var(--gio-muted-3)" }}>No extension snapshot captured.</p> : jffTopSellers.slice(0, 20).map((item, index) => <JffItemRow key={`${item.account_id}:${item.item_title}:${index}`} item={item} quantity={item.reported_qty_sold} amount={item.reported_revenue} />)}
          </div>
          <div className="border-t pt-3" style={{ borderColor: "var(--gio-border)" }}>
            <h3 className="mb-2 text-xs font-semibold" style={{ color: "var(--gio-text)" }}>Detailed Sales · visible-row sample</h3>
            {jffStoreSales.length === 0 ? <p className="py-4 text-xs" style={{ color: "var(--gio-muted-3)" }}>No safe item aggregates captured.</p> : jffStoreSales.slice(0, 20).map((item, index) => <JffItemRow key={`${item.account_id}:${item.item_title}:${index}`} item={item} quantity={item.reported_purchase_count} amount={item.reported_revenue} detail={`${item.first_sale_at || "Date unavailable"} – ${item.last_sale_at || "Date unavailable"}`} />)}
          </div>
        </div>
      </section>}
      {latestMassMessageMetrics.length > 0 && (platformFilter === "all" || platformFilter === "justforfans") && <section className="mt-6 border-y py-4" style={{ borderColor: "var(--gio-border)" }} data-testid="lb-jff-mass-message-report">
        <div className="mb-3">
          <h2 className="text-sm font-bold" style={{ color: "var(--gio-text)" }}>JFF Mass Message Media · {massMessageRangeLabel}</h2>
          <p className="mt-1 text-[10px]" style={{ color: "var(--gio-muted-3)" }}>Platform-reported private-campaign summary · captured {latestMassMessageCapture} · separate from Top Sellers, Detailed Sales, payouts, and leaderboard revenue.</p>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {latestMassMessageMetrics.map(({ key, kind, metric }) => <GrandStat
            key={key} testid={`lb-${key}`} label={massMessageMetricLabels[kind]}
            value={metric.raw_value || "Not reported"} sub="reported summary" tint={PLATFORM_COLORS.justforfans}
          />)}
        </div>
      </section>}
    </AppLayout>
  );
}

function JffItemRow({ item, quantity, amount, detail }) {
  const currency = item.currency;
  const displayAmount = amount === null || amount === undefined
    ? "Not available"
    : currency
      ? new Intl.NumberFormat(undefined, { style: "currency", currency }).format(Number(amount))
      : `${Number(amount).toLocaleString()} · currency unavailable`;
  return <div className="flex items-center justify-between gap-3 border-b py-2" style={{ borderColor: "var(--gio-border)" }}>
    <div className="min-w-0"><div className="truncate text-xs" style={{ color: "var(--gio-text)" }}>{item.item_title}</div>
      <div className="truncate text-[10px]" style={{ color: "var(--gio-muted-3)" }}>{item.account_id} · {detail || `Captured ${item.observed_at || "time unavailable"}`}</div>
    </div>
    <div className="shrink-0 text-right text-xs" style={{ color: "var(--gio-muted-2)" }}>{quantity ?? "Not available"} sold · {displayAmount}</div>
  </div>;
}

function GrandStat({ label, value, icon, testid, accent, tint, sub }) {
  const borderColor = accent
    ? "var(--gio-secondary)"
    : tint || "var(--gio-border)";
  const iconColor = accent
    ? "var(--gio-secondary)"
    : tint || "var(--gio-primary)";
  const valueColor = accent
    ? "var(--gio-secondary)"
    : tint || "var(--gio-text)";
  return (
    <div
      data-testid={testid}
      className="rounded-lg border p-4 flex flex-col gap-1.5 transition hover:-translate-y-0.5"
      style={{ borderColor, background: "var(--gio-surface)" }}>
      <div
        className="overline inline-flex items-center gap-1.5"
        style={{ color: "var(--gio-muted-2)" }}>
        <span style={{ color: iconColor }}>{icon}</span>
        {label}
      </div>
      <div
        className="text-[22px] font-bold leading-none truncate"
        style={{ color: valueColor }}>
        {value}
      </div>
      {sub && (
        <div className="text-[10px]" style={{ color: "var(--gio-muted-3)" }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function PodiumCard({ rank, entry }) {
  if (!entry) return <div />;
  const icon =
    rank === 1 ? (
      <Crown size={22} />
    ) : rank === 2 ? (
      <Medal size={22} />
    ) : (
      <Award size={22} />
    );
  const podiumColor =
    rank === 1 ? "#FFD700" : rank === 2 ? "#C0C0C0" : "#CD7F32";
  const order = rank === 1 ? "md:order-2" : rank === 2 ? "md:order-1" : "md:order-3";
  const scale = rank === 1 ? "md:scale-105" : "";
  const platformColor = PLATFORM_COLORS[entry.platform] || "#94A3B8";

  return (
    <div
      data-testid={`lb-podium-${rank}`}
      className={`rounded-lg border p-4 flex flex-col items-center text-center transition hover:-translate-y-1 ${order} ${scale}`}
      style={{
        borderColor: podiumColor,
        background: "var(--gio-surface)",
        boxShadow:
          rank === 1
            ? `0 0 24px ${podiumColor}33`
            : `0 0 12px ${podiumColor}22`,
      }}>
      <div style={{ color: podiumColor }}>{icon}</div>
      <div
        className="text-[10px] font-mono-ui uppercase tracking-wider mt-1"
        style={{ color: podiumColor }}>
        #{rank} {rank === 1 ? "Top Platform" : rank === 2 ? "Runner-up" : "Bronze"}
      </div>
      <div
        className="w-16 h-16 rounded-full my-3 border-2 flex items-center justify-center font-heading text-xl"
        style={{
          borderColor: platformColor,
          background: `${platformColor}22`,
          color: platformColor,
        }}>
        {entry.name[0]}
      </div>
      <div className="font-heading text-[15px] line-clamp-1">{entry.name}</div>
      <div
        className="text-[22px] font-bold mt-2"
        style={{ color: "var(--gio-secondary)" }}>
        ${entry.total_revenue.toLocaleString()}
      </div>
      <div
        className="text-[10px] mt-1"
        style={{ color: "var(--gio-muted-2)" }}>
        <span style={{ color: "#00AFF0" }}>
          {entry.subscribers.toLocaleString()} subs
        </span>
        {" · "}
        <span style={{ color: "#A855F7" }}>
          {entry.followers.toLocaleString()} followers
        </span>
      </div>
    </div>
  );
}

function Row({ rank, entry }) {
  const rankColor =
    rank === 1
      ? "#FFD700"
      : rank === 2
      ? "#C0C0C0"
      : rank === 3
      ? "#CD7F32"
      : "var(--gio-muted-3)";
  const platformColor = PLATFORM_COLORS[entry.platform] || "#94A3B8";

  return (
    <div
      data-testid={`lb-row-${entry.platform}`}
      className="w-full text-left grid grid-cols-[48px_1fr_120px_120px_140px_36px] gap-3 items-center px-4 py-3 border-b transition hover:bg-[color:var(--gio-primary-soft)]"
      style={{ borderColor: "var(--gio-border)" }}>
      {/* Rank */}
      <div
        className="text-center font-mono-ui font-bold text-[14px]"
        style={{ color: rankColor }}>
        #{rank}
      </div>
      {/* Platform */}
      <div className="flex items-center gap-3 min-w-0">
        <div
          className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center font-bold"
          style={{ background: `${platformColor}22`, color: platformColor }}>
          {entry.name[0]}
        </div>
        <div className="min-w-0">
          <div
            className="font-bold text-[13px] truncate"
            style={{ color: "var(--gio-text)" }}>
            {entry.name}
          </div>
          <div
            className="text-[10px] truncate"
            style={{ color: "var(--gio-muted-3)" }}>
            {entry.posts_count} posts · {entry.transactions.length} transactions
          </div>
        </div>
      </div>
      {/* Subscribers */}
      <div className="text-right">
        <div
          className="text-[13px] font-bold"
          style={{
            color: entry.subscribers > 0 ? "#00AFF0" : "var(--gio-muted-3)",
          }}>
          {entry.subscribers.toLocaleString()}
        </div>
        <div className="text-[10px]" style={{ color: "var(--gio-muted-3)" }}>
          subscribers
        </div>
      </div>
      {/* Followers */}
      <div className="text-right">
        <div
          className="text-[13px] font-bold"
          style={{
            color: entry.followers > 0 ? "#A855F7" : "var(--gio-muted-3)",
          }}>
          {entry.followers.toLocaleString()}
        </div>
        <div className="text-[10px]" style={{ color: "var(--gio-muted-3)" }}>
          followers
        </div>
      </div>
      {/* Revenue */}
      <div className="text-right">
        <div
          className="text-[16px] font-bold"
          style={{ color: "var(--gio-secondary)" }}>
          ${entry.total_revenue.toLocaleString()}
        </div>
        <div className="text-[10px]" style={{ color: "var(--gio-muted-3)" }}>
          ledger revenue
        </div>
        {entry.platform === "justforfans" && entry.reported_total_revenue !== null && (
          <div className="mt-1 text-[9px] leading-tight" data-testid="lb-jff-reported-revenue"
            style={{ color: "var(--gio-muted-3)" }}>
            Reported lifetime ${Number(entry.reported_total_revenue).toLocaleString()} · excluded
          </div>
        )}
      </div>
      <ExternalLink
        size={12}
        className="shrink-0"
        style={{ color: "var(--gio-muted-3)" }}
      />
    </div>
  );
}
